import React, { useEffect, useState, Fragment } from "react";
import { UpdateAccountMetadata } from "@/Utils/Promises";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Icon,
  Input,
  Select,
  Modal,
  Menu,
  Text,
  Checkbox,
  Divider,
  Counter,
  List,
  TextArea
} from "@theorgchartdev/storybook";
import cx from "classnames";
import { GetNewProfile } from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/Security";
import {
  SetBranchSecProfileToJSON,
  getProfileNameError
} from "@/appComponents/Blocks/Modals/SettingsModal/AccountSettings/Security/SecurityUtils";
import SectionCard, {
  SectionCardGroup
} from "@/appComponents/Blocks/Composites/SectionCard/SectionCard";
import BasicChartSearchMenu from "@/appComponents/Blocks/Menus/BasicChartSearchMenu/BasicChartSearchMenu";
import { appstr } from "@coreapp/utils/appstr";
import { QAlert } from "@coreapp/CoreApp/QAlert";
import { appstrp } from "@coreapp/utils/appstrp";
import { kPositionTitleID, kNameID, enumObjectTypes } from "@coreapp/constants";

const branchSecOptions = ["selfsubs", "ownbranch", "enumeratedbranch"];

export const BranchLevelSecurityModal = ({
  show,
  existingProfileID,
  accountConfig,
  profiles,
  onClose,
  isCreateNewProfile
}) => {
  const [editProfile, setEditProfile] = useState(null);
  const [clonedProfile, setClonedProfile] = useState(null);
  const [isNewProfile, setIsNewProfile] = useState(isCreateNewProfile);
  const [changedFields, setChangedFields] = useState([]);
  const [errorStatus, setErrorStatus] = useState("");

  useEffect(() => {
    InitializeProfile();
  }, []);

  const updateProfile = async data => {
    if (!data) return;
    const { result, success } = await UpdateAccountMetadata(data);
    if (result && success) {
      setClonedProfile(JSON.parse(JSON.stringify(editProfile)));
      clearDirty();
      onClose(editProfile, existingProfileID);
    } else {
      var w = new QAlert();
      w.InitAlert(appstr("Alert"), appstr("groupupdateerr"), null, GetWSMgr(), true);
    }
  };

  const InitializeProfile = () => {
    let initProfile = null;
    if (existingProfileID != null && profiles[existingProfileID] && !isNewProfile) {
      initProfile = profiles[existingProfileID];
    } else if (isNewProfile) {
      let { newProfile } = GetNewProfile(accountConfig["security"], "branchlevel");
      initProfile = newProfile;
      setIsNewProfile(true);
    }
    if (initProfile != null) {
      setClonedProfile(JSON.parse(JSON.stringify(initProfile)));
      setEditProfile(JSON.parse(JSON.stringify(initProfile)));
    }
  };

  const handleChange = (key, value) => {
    setEditProfile({
      ...editProfile,
      [key]: value
    });
    ChangedField(key);
  };

  const ChangedField = key => {
    if (!changedFields.includes(key)) setChangedFields([...changedFields, key]);
  };

  const handleInputChange = e => {
    const value = e.target.value;
    const key = e.target.name;
    if (key === "name" && errorStatus) setErrorStatus("");
    handleChange(key, value);
  };

  const checkDirty = () => {
    return changedFields?.length > 0;
  };

  const clearDirty = () => {
    setChangedFields([]);
  };

  const checkName = () => {
    return getProfileNameError(
      accountConfig["security"],
      editProfile.name,
      "branchlevel",
      existingProfileID
    );
  };

  const GetAvailableFields = () => {
    let headers = [];
    const curDoc = GetDoc();
    if (curDoc) {
      if (curDoc.IsPositionDocument()) {
        headers = curDoc.GetTargetHeaders(true, false, true, true, [], curDoc.GetPositionClass());
      } else {
        headers = curDoc.GetTargetHeaders(true, false, true, true, []);
      }
    }
    return headers;
  };

  const onSaveProfile = () => {
    let nameMsg = checkName();
    if (nameMsg.length > 0) {
      setErrorStatus(nameMsg);
      return;
    }
    setErrorStatus("");
    let updatedSecurityProfiles = SetBranchSecProfileToJSON(
      accountConfig,
      { ...editProfile, name: editProfile.name.trim() },
      existingProfileID
    );
    let curSecObj = accountConfig["security"];
    if (!curSecObj || !curSecObj["branchlevel"])
      var securityDump = { branchlevel: updatedSecurityProfiles };
    else {
      curSecObj["branchlevel"] = updatedSecurityProfiles;
      securityDump = curSecObj;
    }
    const theData = JSON.stringify({ security: securityDump });
    updateProfile(theData);
  };

  const onCancel = (skipDirtyCheck = false) => {
    const wsMgr = GetWSMgr();
    if (checkDirty() && !skipDirtyCheck) {
      wsMgr.CreateReactAlert(
        appstr("Save changes to") + editProfile["name"] + "?",
        appstr("All changes will be lost"),
        () => onSaveProfile(),
        () => onCancel(true),
        appstr("Save Changes"),
        appstr("Discard Changes")
      );
      return;
    }
    clearDirty();
    setEditProfile(clonedProfile);
    onClose(false);
  };

  const GetFieldCopy = (key, defaultValue) => {
    return editProfile && editProfile[key]
      ? JSON.parse(JSON.stringify(editProfile[key]))
      : defaultValue;
  };

  const GetAvailableBranchOptions = () => {
    let existingRules = [];
    if (editProfile?.rules) {
      existingRules = editProfile.rules.map(item => item.type);
    }
    return branchSecOptions.filter(type => existingRules.indexOf(type) < 0);
  };

  const BuildBranchRuleOptions = () => {
    let branchOptions = [];
    let availableOptions = GetAvailableBranchOptions();
    for (let branchRule of availableOptions) {
      let option = {};
      option.value = branchRule;
      option.label = appstr(branchRule);
      option.clickHandler = AddRule;
      option.dataTestId = `account-settings-security-bls-add-rule-${branchRule}-option`;
      branchOptions.push(option);
    }
    return branchOptions;
  };

  const onBranchPruneToggle = () => {
    handleChange("branchpruning", !editProfile.branchpruning);
  };

  const AddRule = o => {
    let newRule = { type: o.value };

    switch (newRule.type) {
      case "ownbranch":
        newRule["restrictField"] = null;
        break;
      case "enumeratedbranch":
        newRule["topboxids"] = [];
        break;
    }

    let existingRules = GetFieldCopy("rules", []);
    existingRules.push(newRule);

    handleChange("rules", existingRules);
  };

  const RemoveRule = index => {
    let ruleCopy = GetFieldCopy("rules", []);
    ruleCopy.splice(index, 1);
    handleChange("rules", ruleCopy);
  };

  const SetBranchField = (field, index) => {
    let clonedRules = GetFieldCopy("rules", []);
    let updatedRule = clonedRules[index];
    if (!updatedRule) return;
    updatedRule["restrictField"] = field;
    clonedRules[index] = updatedRule;
    handleChange("rules", clonedRules);
  };

  const BuildBranchFieldOptions = ruleIndex => {
    let fieldOptions = [];
    let selectedFields = GetAvailableFields();
    if (!selectedFields) return fieldOptions;

    for (let field of selectedFields) {
      let option = {};
      option.value = field;
      option.label = field;
      option.clickHandler = () => SetBranchField(field, ruleIndex);
      fieldOptions.push(option);
    }
    return fieldOptions;
  };

  const UpdateAdditionalLevels = (newValue, index) => {
    let clonedRules = GetFieldCopy("rules", []);
    let updatedRule = clonedRules[index];
    if (!updatedRule) return;
    updatedRule["additionalLevels"] = parseInt(newValue);
    clonedRules[index] = updatedRule;
    handleChange("rules", clonedRules);
  };

  const AddBranch = (e, index) => {
    const topboxObj = e.result;
    if (!topboxObj) return;

    let clonedRules = GetFieldCopy("rules", []);
    let updatedRule = clonedRules[index];
    if (!updatedRule) return;

    const personID = e.value;
    const personName =
      topboxObj?.objType === enumObjectTypes.position
        ? topboxObj[kPositionTitleID]
        : topboxObj[kNameID];

    if (updatedRule["topboxids"] == null) updatedRule["topboxids"] = [];
    let existingIDs = updatedRule["topboxids"];
    if (existingIDs.indexOf(personID) === -1) existingIDs.push(personID);
    if (updatedRule["topboxnamemap"] == null) updatedRule["topboxnamemap"] = {};
    updatedRule["topboxnamemap"][personID] = personName;

    clonedRules[index] = updatedRule;
    handleChange("rules", clonedRules);
  };

  const RemoveBranch = (personID, ruleIndex) => {
    let clonedRules = GetFieldCopy("rules", []);
    let updatedRule = clonedRules[ruleIndex];
    if (!updatedRule) return;

    let existingIDs = updatedRule["topboxids"];
    let idx = existingIDs.indexOf(personID);
    existingIDs.splice(idx, 1);

    updatedRule["topboxids"] = existingIDs;

    if (updatedRule["topboxnamemap"]) delete updatedRule["topboxnamemap"][personID];

    clonedRules[ruleIndex] = updatedRule;
    handleChange("rules", clonedRules);
  };

  const BuildBranches = (rule, ruleIndex) => {
    let branches = [];
    let ids = rule.topboxids ?? [];
    let nameMap = rule.topboxnamemap ?? {};
    for (let personID of ids) {
      let label = nameMap[personID] ?? "<No Name>";
      label += " (" + personID + ")";
      let option = {};
      option.value = personID;
      option.content = (
        <ActionListItem
          label={label}
          labelProps={{
            size: "small"
          }}
          actions={[
            {
              iconName: "trash-can",
              iconProps: { color: "error" },
              onClick: () => RemoveBranch(personID, ruleIndex),
              dataTestId: `account-settings-security-bls-branch-${personID}-remove-btn`
            }
          ]}
        />
      );
      option.divider = true;
      branches.push(option);
    }
    return branches;
  };

  const GetLevelsCounter = (rule, index) => {
    if (!rule) return;
    return (
      <Flex direction={"column"} gap={"8px"}>
        <Text size={"small"} modifier={"bold"}>
          {appstr("Additional Levels")}
        </Text>
        <span style={{ flexGrow: "0", marginLeft: "0", marginRight: "auto" }}>
          <Counter
            onChange={newValue => UpdateAdditionalLevels(newValue, index)}
            currentValue={rule.additionalLevels ?? 0}
            defaultValue={0}
            minValue={0}
            size={"small"}
            dataTestIdPrefix={`account-settings-security-bls-rule-${rule.type}-levels-counter`}
          />
        </span>
      </Flex>
    );
  };

  const GetRuleContent = (rule, index) => {
    const doc = GetDoc();
    const isPosition = doc && doc.IsPositionDocument();
    let content = null;
    switch (rule.type) {
      case "ownbranch":
      // note: branch access field option has been removed. we will still allow existing configuration here.
      case "branchaccesslist":
        let field = rule["restrictField"] ?? appstr("None");
        content = (
          <Flex direction={"column"} gap={"16px"}>
            {rule.type === "ownbranch" && GetLevelsCounter(rule, index)}
            <Select
              listProps={{ enableVirtualization: false, ...FieldSelectListProps() }}
              inputOnly={false}
              label={isPosition ? appstr("Position field") : appstr("Field")}
              selectSize={"medium"}
              value={field}
              options={BuildBranchFieldOptions(index)}
              dataTestId={`account-settings-security-bls-rule-${rule.type}-restrict-field-select`}
            />
          </Flex>
        );
        break;
      case "selfsubs":
        content = GetLevelsCounter(rule, index);
        break;
      case "enumeratedbranch":
        content = (
          <Flex direction={"column"} gap={"8px"} width={"100%"}>
            <Flex direction={"column"} gap={"8px"}>
              <Text modifier={"medium"} size={"small"}>
                {appstr("topbox")}
              </Text>
              <BasicChartSearchMenu
                id={"append-entry-search"}
                inputSize={"medium"}
                inputProps={{
                  placeholder: appstrp("Find branches", "..."),
                  leftIcon: true
                }}
                dataTestId={`account-settings-security-bls-rule-${rule.type}-branch-search`}
                handleSearchOptionClick={option => {
                  AddBranch(option, index);
                }}
              />
            </Flex>
            <List
              enableVirtualization={false}
              optionSize={"medium"}
              options={BuildBranches(rule, index)}
              listWidth="fill"
            />
          </Flex>
        );
    }

    const removeButton = (
      <Flex className={"oc-right-justify"}>
        <Button
          modifier={"secondary"}
          onClick={() => RemoveRule(index)}
          size={"medium"}
          dataTestId={`account-settings-security-bls-rule-${rule.type}-remove-btn`}
        >
          {appstr("Remove")}
        </Button>
      </Flex>
    );
    return (
      <SectionCard
        key={rule.type + "_" + index.toString()}
        title={appstr(rule.type)}
        titleContent={removeButton}
        customStyle={{ padding: "10px" }}
      >
        <div style={{ padding: "0 16px 8px 16px" }}>{content}</div>
      </SectionCard>
    );
  };

  const availableOptions = GetAvailableBranchOptions();
  const addRuleTrigger = (
    <Button
      size={"medium"}
      className={cx("oc-special-secondary", "oc-special-secondary--fit")}
      modifier={"secondary"}
      tooltipProps={{ message: appstr("Create new conditional rule"), position: "top" }}
      iconProps={{ name: "plus" }}
      disabled={availableOptions.length === 0}
      dataTestId="account-settings-security-bls-add-rule-btn"
    >
      {appstr("Branch rule")}
    </Button>
  );

  return (
    <Fragment>
      <CentralizedModalContainer show={show}>
        <Modal
          id="example-modal"
          show={show}
          minWidth={"medium"}
          maxWidth={"medium"}
          fillWidth={true}
          centerScreen={true}
        >
          <DialogTitle
            divider={true}
            onClose={() => onCancel()}
            data-testid="account-settings-security-bls-modal-close-btn"
          >
            <span className={"ocn-explorer-icon-label"}>
              <Icon size={"medium"} name={isNewProfile ? "user-plus" : "user-pen"} />
              {isNewProfile ? appstr("Create security profile") : appstr("Edit security profile")}
            </span>
          </DialogTitle>
          <DialogContent customContentClass={"oc-modal-content-container"}>
            <Flex
              gap={"16px"}
              overflow={"hidden"}
              direction={"column"}
              padding={"24px 16px"}
              height={"100%"}
              className={"oc-modal-content"}
            >
              <Flex gap={"4px"} direction={"column"}>
                <Input
                  label={appstr("Name")}
                  name={"name"}
                  placeholder={appstr("Name")}
                  size={"medium"}
                  value={editProfile?.name}
                  onChange={handleInputChange}
                  status={errorStatus ? "error" : "default"}
                  message={errorStatus}
                  dataTestId="account-settings-security-bls-name-input"
                />
                <TextArea
                  label={appstr("Description")}
                  name={"description"}
                  placeholder={appstr("Description")}
                  size={"medium"}
                  value={editProfile?.description}
                  onChange={handleInputChange}
                  dataTestId="account-settings-security-bls-description-textarea"
                />
              </Flex>
              <Divider vertical={false} />
              <Flex gap={"8px"} overflow={"hidden"} flex={"1"} justifyContent={"space-between"}>
                <Flex gap={"24px"} width={"100%"} height={"100%"} overflow={"hidden"}>
                  <Flex
                    direction={"column"}
                    gap={"12px"}
                    width={"100%"}
                    height={"100%"}
                    overflow={"hidden"}
                  >
                    <Flex justifyContent={"space-between"} alignItems={"flex-end"}>
                      <Flex direction={"column"} gap={"12px"} flex={"1"}>
                        <Flex
                          justifyContent={"space-between"}
                          gap={"4px"}
                          alignItems={"center"}
                          width={"100%"}
                        >
                          <Text modifier={"bold"} size={"medium"}>
                            {appstr("Rules")}
                          </Text>
                        </Flex>
                        <Checkbox
                          key={"branch-prune"}
                          id={"branch-prune-cb"}
                          data-testid="account-settings-security-bls-branch-pruning-checkbox"
                          size="medium"
                          label={appstr("Branch pruning")}
                          tooltipProps={{ message: appstr("Branch pruning info") }}
                          checked={editProfile?.branchpruning === true}
                          onToggle={() => onBranchPruneToggle()}
                        />
                      </Flex>
                      <Flex className={"oc-right-justify"} padding={"0 4px 0 0"}>
                        <Menu
                          options={BuildBranchRuleOptions()}
                          position={"bottom-end"}
                          trigger={addRuleTrigger}
                          disabled={availableOptions.length === 0}
                        />
                      </Flex>
                    </Flex>
                    {editProfile?.rules && editProfile.rules.length > 0 && (
                      <div
                        className={cx("oc-list-container", "ocn-list-scrollbar")}
                        style={{ backgroundColor: "#f8f8f9", width: "100%" }}
                      >
                        <SectionCardGroup
                          addMarginAbove={false}
                          addMarginBelow={false}
                          customStyle={{ width: "100%" }}
                          flexProps={{ gap: "0" }}
                        >
                          {editProfile?.rules?.map((rule, index) => (
                            <div key={index}> {GetRuleContent(rule, index)} </div>
                          ))}
                        </SectionCardGroup>
                      </div>
                    )}
                    {(!editProfile?.rules ||
                      (editProfile?.rules && editProfile.rules.length === 0)) && (
                      <Menu
                        trigger={
                          <Button
                            modifier="underline"
                            size="medium"
                            type="button"
                            underline
                            dataTestId="account-settings-security-bls-no-conditions-add-btn"
                          >
                            {appstr("No rule conditions")}
                          </Button>
                        }
                        options={BuildBranchRuleOptions()}
                        position={"bottom-start"}
                      />
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </DialogContent>
          <DialogActions divider={true}>
            <DialogActions.Tertiary
              onClick={() => onCancel()}
              dataTestId="account-settings-security-bls-modal-cancel-btn"
            >
              {appstr("Cancel")}
            </DialogActions.Tertiary>
            <DialogActions.Primary
              onClick={() => onSaveProfile()}
              dataTestId="account-settings-security-bls-modal-save-btn"
            >
              {isNewProfile ? appstr("Create") : appstr("Save")}
            </DialogActions.Primary>
          </DialogActions>
        </Modal>
      </CentralizedModalContainer>
    </Fragment>
  );
};
